import React from "react"
import { useHistory } from "react-router-dom"

import { Button } from "@/components/ui/button"
import { UploadIcon, type UploadIconHandle } from "@/components/ui/upload-icon"
import { cn } from "@/lib/utils"

export interface DashboardHeaderImportButtonProps {
  /** Icon square only (e.g. dashboard mobile header). */
  iconOnly?: boolean
}

/**
 * Dashboard inset header Import — shadcn `Button` outline-brand + animated `UploadIcon`.
 */
export function DashboardHeaderImportButton({
  iconOnly = false,
}: DashboardHeaderImportButtonProps = {}): React.ReactElement {
  const history = useHistory()
  const iconRef = React.useRef<UploadIconHandle | null>(null)

  const startIconAnimation = React.useCallback(() => {
    void iconRef.current?.startAnimation()
  }, [])

  const stopIconAnimation = React.useCallback(() => {
    void iconRef.current?.stopAnimation()
  }, [])

  const iconHoverHandlers = React.useMemo(
    () => ({
      onPointerDownCapture: startIconAnimation,
      onMouseEnter: startIconAnimation,
      onMouseLeave: stopIconAnimation,
    }),
    [startIconAnimation, stopIconAnimation]
  )

  return (
    <Button
      type="button"
      variant="outline-brand"
      size={iconOnly ? "icon" : "sm"}
      data-font="primary"
      className={cn(iconOnly ? "h-8 w-8 shrink-0" : "shrink-0")}
      onClick={() => history.push("/profile/broker-api")}
      aria-label="Open Broker Management in Profile"
      {...iconHoverHandlers}
    >
      <UploadIcon
        ref={iconRef}
        data-icon={iconOnly ? undefined : "inline-start"}
        size={16}
        className="shrink-0 text-current"
        aria-hidden
      />
      {iconOnly ? null : "Import"}
    </Button>
  )
}

/** @deprecated Use `DashboardHeaderImportButton` — kept for existing imports. */
export function DashboardHeaderImportButtonThemed(): React.ReactElement {
  return <DashboardHeaderImportButton />
}
